// Web Push helpers — subscribe, unsubscribe, and settings I/O.
//
// The whole subscribe flow:
//   1. registerServiceWorker() — install /sw.js on first load.
//   2. subscribe() — ask the browser for Notification permission, use the
//      server's VAPID public key to create a PushSubscription, and POST
//      the (endpoint, p256dh, auth) triple to /api/push/subscribe.
//   3. unsubscribe() — mirror-image cleanup.
//
// Everything is fault-tolerant: we no-op on browsers without Push API
// support (older iOS Safari, some in-app browsers) so the UI degrades
// gracefully rather than throwing.

import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/** Convert the URL-safe base64 VAPID key to the Uint8Array subscribe() wants. */
function urlBase64ToUint8Array(b64) {
  const padding = "=".repeat((4 - (b64.length % 4)) % 4);
  const base64 = (b64 + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

/** Feature detection. Push requires Service Worker + Push API + Notifications. */
export function isPushSupported() {
  return (
    typeof window !== "undefined" &&
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window
  );
}

/** True when the site is running as an installed PWA (matters for iOS). */
export function isStandalonePwa() {
  if (typeof window === "undefined") return false;
  if (window.matchMedia && window.matchMedia("(display-mode: standalone)").matches) return true;
  // iOS Safari legacy flag.
  return !!window.navigator.standalone;
}

/** True on iOS (any browser — Safari, Chrome, etc. all use WebKit there). */
export function isIOS() {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  return /iPad|iPhone|iPod/.test(ua) || (ua.includes("Macintosh") && "ontouchend" in document);
}

let _swReg = null;

/**
 * Register the service worker. Idempotent — safe to call from index.js on
 * every boot; the browser reuses the existing registration if one exists.
 */
export async function registerServiceWorker() {
  if (!isPushSupported()) return null;
  if (_swReg) return _swReg;
  try {
    _swReg = await navigator.serviceWorker.register("/sw.js", { scope: "/" });
    return _swReg;
  } catch (err) {
    console.warn("[push] service worker registration failed:", err);
    return null;
  }
}

async function getReg() {
  await registerServiceWorker();
  if (!_swReg) return null;
  // Wait until the worker is active so PushManager.subscribe won't error.
  if (_swReg.installing || _swReg.waiting) {
    await new Promise((r) => setTimeout(r, 400));
  }
  return _swReg;
}

/** Current permission state — "default" | "granted" | "denied" | "unsupported". */
export function permissionState() {
  if (!isPushSupported()) return "unsupported";
  return Notification.permission;
}

/**
 * Subscribe the browser to Web Push. Handles the whole handshake:
 * permission → pushManager.subscribe → POST to backend.
 *
 * Returns:
 *   { ok: true,  subscription, settings }  on success
 *   { ok: false, reason }                   on any failure
 */
export async function subscribeToPush() {
  if (!isPushSupported()) return { ok: false, reason: "unsupported" };

  // On iOS, the Push API is only available inside an installed PWA.
  if (isIOS() && !isStandalonePwa()) return { ok: false, reason: "ios-not-installed" };

  const perm = await Notification.requestPermission();
  if (perm !== "granted") return { ok: false, reason: "denied" };

  const reg = await getReg();
  if (!reg) return { ok: false, reason: "no-sw" };

  // Re-use an existing subscription so we don't churn keys unnecessarily.
  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    try {
      const { data } = await axios.get(`${API}/push/vapid-public-key`);
      const key = urlBase64ToUint8Array(data.public_key);
      sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: key,
      });
    } catch (err) {
      console.warn("[push] subscribe failed:", err);
      return { ok: false, reason: "subscribe-failed" };
    }
  }

  const raw = sub.toJSON();
  try {
    const { data } = await axios.post(`${API}/push/subscribe`, {
      endpoint: raw.endpoint,
      keys: raw.keys,
      tz: Intl.DateTimeFormat().resolvedOptions().timeZone || "",
      ua: navigator.userAgent || "",
    });
    return { ok: true, subscription: raw, settings: data.settings };
  } catch (err) {
    console.warn("[push] backend subscribe failed:", err);
    return { ok: false, reason: "backend" };
  }
}

/** Cancel the browser subscription and delete it on the server. */
export async function unsubscribeFromPush() {
  if (!isPushSupported()) return { ok: true };
  const reg = await getReg();
  if (!reg) return { ok: true };
  const sub = await reg.pushManager.getSubscription();
  if (!sub) return { ok: true };
  const endpoint = sub.endpoint;
  try { await sub.unsubscribe(); } catch { /* noop */ }
  try { await axios.post(`${API}/push/unsubscribe`, { endpoint }); } catch { /* noop */ }
  return { ok: true };
}

/** True if the browser currently holds an active subscription. */
export async function hasActiveSubscription() {
  if (!isPushSupported()) return false;
  const reg = await getReg();
  if (!reg) return false;
  const sub = await reg.pushManager.getSubscription();
  return !!sub;
}

// ----------------------------------------------------- settings I/O
export async function fetchPushSettings() {
  const { data } = await axios.get(`${API}/push/settings`);
  return data;
}

export async function updatePushSettings(patch) {
  const { data } = await axios.put(`${API}/push/settings`, patch);
  return data;
}

export async function sendTestPush() {
  const { data } = await axios.post(`${API}/push/test`);
  return data;
}

// ----------------------------------------------------- deep-link handoff
// When the user taps a notification while a tab is already open, sw.js
// posts an { type: "n8-open", url } message to that tab. We listen once
// and navigate the app accordingly.
export function installOpenHandler(navigate) {
  if (typeof navigator === "undefined" || !navigator.serviceWorker) return;
  navigator.serviceWorker.addEventListener("message", (ev) => {
    const msg = ev.data || {};
    if (msg.type === "n8-open" && msg.url) {
      try {
        const u = new URL(msg.url, window.location.origin);
        // Strip any hash router prefix ("/#/foo") back to "/foo".
        const path = u.hash?.startsWith("#/") ? u.hash.slice(1) : u.pathname;
        navigate?.(path);
      } catch { /* noop */ }
    }
  });
}
